import type {
  MasterData,
  Country,
  CountryRegion,
  Carrier,
  CountryStateGroup,
  StateProvince,
  ResourceListItem,
  ResourceList,
  DocumentList,
  Question,
  QuestionEntry,
  Questionnaire,
  CodeTypeItem,
  CodeTypeList,
  EquipmentDetail,
  MealsList,
  EquipmentTypesList,
  FareBrandsList,
  ExceptionList,
  RouteMessage,
  RouteMessagesList,
  Announcement,
  AnnouncementsList,
  OlciBanner,
  OlciBanners,
  OfferCarousel,
  OfferCarouselsList,
  LifeBanner,
  LifeBannersList,
  UpgradeOffer,
  UpgradeOffersList,
  DetailUpgradeList,
  RandomSeatSelectionRules,
  AppSettings,
} from '@models/master-data/master-data.model';

// ── Airport ──────────────────────────────────────────────────────────────────

export interface AirportDto {
  category: Category[];
}
export interface CountryMetaRegionDto {
  key: string;
  title: string;
  description?: string;
}

export interface CountryMetaDto {
  region?: CountryMetaRegionDto[];
  alphaNumeric3?: string;
  isoNumeric?: string;
  telephoneCode?: string;
  websiteLanguage?: string;
  isDisabled?: string;
}

export interface CountryItemDto {
  key: string;
  value: string;
  description: string;
  meta?: CountryMetaDto;
}

interface CountryCategoryDto {
  name: string;
  item: CountryItemDto[];
}

export interface CountryDto {
  category: CountryCategoryDto[];
}

// ── GroupedStates ─────────────────────────────────────────────────────────────

export interface StateProvinceDto {
  code: string;
  name: string;
}

export interface GroupedStateItemDto {
  country: {
    code: string;
    name: string;
  };
  stateList: StateProvinceDto[];
}

export type GroupedStatesDto = GroupedStateItemDto[];

// ── Carrier ───────────────────────────────────────────────────────────────────

export interface CarrierItemDto {
  carrierCode: string;
  carrierName: string;
  carrierDescription: string;
  column1DesktopImage: string;
  tailImage: string;
  tailImageTransparent?: string;
  carrierSite?: string;
}

export interface CarrierDto {
  carriers: CarrierItemDto[];
}

// ── ResourceList ──────────────────────────────────────────────────────────────

export interface ResourceListItemDto {
  key: string;
  value: string;
}

export interface ResourceListGroupDto {
  name: string;
  item: ResourceListItemDto[];
}

export interface ResourceListDto {
  resourceLists: ResourceListGroupDto[];
}

// ── Questionnaire ────────────────────────────────────────────────────────

export interface QuestionnaireItemDto {
  Value: string;
  LinkText?: string;
  MobileLink?: {
    MobileLinkKey?: string;
    MobileLinkValue?: string;
  };
  PopoverKey: string;
  PopoverValue: string;
  ImageUrl?: string;
  TBImageUrl?: string;
  MBImageUrl?: string;
  IsTim: boolean;
}

export interface QuestionnaireEntryDto {
  QuestionniareKey: string;
  QuestionniareItems: QuestionnaireItemDto[];
}

export type QuestionnaireDto = Record<string, QuestionnaireEntryDto>;

// ── CodeTypeList ──────────────────────────────────────────────────────────────

export interface EquipmentDetailDto {
  title: string;
  description: string;
}

export interface CodeTypeItemDto {
  codeID: string;
  codeType?: string;
  codeName?: string;
  shortCodeName?: string;
  mobileShortCodeName?: string;
  qty?: string;
  group?: string;
  bookingReceiptImage?: string;
  receiptDisplay?: string;
  reviewDisplay?: string;
  shortDescription?: string;
  longDescription?: string;
  codeDescription?: string;
  cartDisplayFormat?: string;
  cartDisplay?: string;
  displayGroup?: string;
  ssrIconImage?: string;
  equipmentDetails?: EquipmentDetailDto[];
}

export interface CodeTypeListDto {
  codeTypeList: CodeTypeItemDto[];
}

// ── Meals ────────────────────────────────────────────────────────────────────

export interface MealItemDto {
  name: string;
  code: string;
  description?: string;
  masterCode?: string;
}

export interface MealsListDto {
  meals: MealItemDto[];
}

// ── Equipment Types ──────────────────────────────────────────────────────────

export interface EquipmentTypeItemDto {
  key: string;
  value: string;
  description: string;
  meta?: Record<string, unknown>;
}

export interface EquipmentTypesCategoryDto {
  name: string;
  item: EquipmentTypeItemDto[];
}

export interface EquipmentTypesListDto {
  category: EquipmentTypesCategoryDto[];
}

// ── Fare Brands ──────────────────────────────────────────────────────────────

export interface FareBrandPolicyDto {
  key: string;
  value: string;
}

export interface FareBrandIncludesDto {
  dateChange?: string;
  dateChangePolicies?: string[];
  cancellation?: string;
  cancellationPolicies?: string[];
}

export interface FareBrandItemDto {
  code: string;
  name: string;
  longDescription?: string;
  alternateDisplay?: string;
  receiptDisplay?: string;
  priority?: string | null;
  includes?: FareBrandIncludesDto;
  rules?: FareBrandPolicyDto[];
}

export interface FareBrandsDto {
  fareBrands: FareBrandItemDto[];
  includedExtras?: Record<string, unknown>[];
  cancellationRules?: Record<string, unknown>;
  changeFeesRules?: Record<string, unknown>;
  categories?: Record<string, unknown>[];
}

// ── Exception Messages ──────────────────────────────────────────────────────

export type ExceptionListDto = Record<string, string>;

// ── Route Messages ──────────────────────────────────────────────────────────

export interface RouteMessageItemDto {
  key: string;
  title: string;
  message: string;
}

export interface RouteMessagesDto {
  routeMessages: RouteMessageItemDto[];
}

export interface AnnouncementsDto {
  routeMessages: RouteMessageItemDto[];
}

export interface OlciBannerDto {
  code: string;
  desktopImage: string;
  mobileImage: string;
  url: string | null;
}

export type OlciBannersDto = OlciBannerDto[];

// ── Offer Carousels ─────────────────────────────────────────────────────────────────

export interface OfferCarouselItemDto {
  image: string;
  mobileImage: string;
  title: string;
  description: string;
  buttonText: string;
  link: string;
}

export type OfferCarouselsDto = OfferCarouselItemDto[];

// ── Life Banners ────────────────────────────────────────────────────────────────────────

export interface LifeBannerItemDto {
  code: string;
  desktopImage: string;
  mobileImage: string;
  url: string;
}

export type LifeEshopDto = LifeBannerItemDto[];

// ── Upgrade Offers ──────────────────────────────────────────────────────────────────

export interface UpgradeOfferItemDto {
  image: string;
  mobileImage: string;
  title: string;
  offer: string;
  buttonText: string;
  footer: string;
  desktopAlignment: string;
}

export type UpgradeOffersDto = UpgradeOfferItemDto[];
// ── Detail Upgrades ─────────────────────────────────────────────────────────────────────────────────

export interface DetailUpgradeItemDto {
  title: string;
  subtitle: string;
  image: string;
}

export interface DetailUpgradeRowDto {
  name: string;
  rows: DetailUpgradeItemDto[][];
}

export type DetailUpgradesDto = DetailUpgradeRowDto[];

// ── Random Seat Selection Rules ────────────────────────────────────────────────────

export interface RandomSeatSelectionRulesDto {
  description1: string;
  description2: string;
  description3: string;
}

// ── App Settings ─────────────────────────────────────────────────────────────────────

export interface AppSettingsItemDto {
  IfeEnabled: string;
  SportsEnabled: string;
  redemptionEnabled: string;
  IsDeepLinkEnabled: string;
  VwoEnable: string;
  DefaultSortOrder: string;
  ConsumerSortOrder: string;
  TASortOrder: string;
  ConsumerConnectionSortOrder: string;
  VwoAdditionalReference: string;
  ['scandIT-license']?: string;
  ScandITErrorTimeOut?: string;
  homepageUrl: string;
  skywardsBenefits: string;
  skywardsEarning: string;
  skywardsSpending: string;
  skywardsMembershipTiers: string;
  skywardsPartners: string;
  skywardsFaq: string;
  skywardsContact: string;
  skywardsTerms: string;
  skywardsMembershipNumberMinLength?: string;
  gtmId?: string;
}

export type AppSettingsDto = Record<string, AppSettingsItemDto>;

// ── Terminal ──────────────────────────────────────────────────────────────────

export interface TerminalItemDto {
  terminal: string;
  display: string;
  context?: unknown[];
}

export type TerminalsDto = TerminalItemDto[];

// ── Composite DTO ────────────────────────────────────────────────────────────────────
export interface Category {
  name: string;
  item: Item[];
}

export interface Item {
  key: string;
  value: string;
  description: string;
  searchKey: string;
  city: string;
  cityEn: string;
  ll: string;
  c: string;
  country: string;
}

export interface MasterDataDto {
  airports: AirportDto;
  countries?: CountryDto;
  groupedStates?: GroupedStatesDto;
  carriers?: CarrierDto;
  resourceLists?: ResourceListDto;
  documentList?: ResourceListDto;
  questionnaire?: QuestionnaireDto;
  codeTypeList?: CodeTypeListDto;
  mealsList?: MealsListDto;
  equipmentTypesList?: EquipmentTypesListDto;
  fareBrands?: FareBrandsDto;
  exceptionList?: ExceptionListDto;
  routeMessages?: RouteMessagesDto;
  announcements?: AnnouncementsDto;
  olciBanners?: OlciBannersDto;
  offers?: OfferCarouselsDto;
  lifeBanners?: LifeEshopDto;
  upgradeOffers?: UpgradeOffersDto;
  detailUpgrades?: DetailUpgradesDto;
  randomSeatSelectionRules?: RandomSeatSelectionRulesDto;
  appSettings?: AppSettingsDto;
  terminals?: TerminalsDto;
}

// ── Mappers ──────────────────────────────────────────────────────────────────

const mapCountryRegions = (regions?: CountryMetaRegionDto[]): CountryRegion[] | undefined =>
  regions?.map(({ key, title, description }) => ({ key, title, description }));

const mapCountries = (dto?: CountryDto, language?: string): Country[] =>
  dto?.category?.flatMap((cat) =>
    cat.item.map(
      (item): Country =>
        ({
          code: item.key,
          name: language === 'en' ? item.value : item.description,
          englishName: item.value,
          dialCode: item.meta?.telephoneCode ? `+${item.meta.telephoneCode}` : '',
          alphaNumeric3: item.meta?.alphaNumeric3,
          isoNumeric: item.meta?.isoNumeric,
          websiteLanguage: item.meta?.websiteLanguage,
          isDisabled: item.meta?.isDisabled === 'True',
          regions: mapCountryRegions(item.meta?.region),
        }) as Country,
    ),
  ) ?? [];

const mapGroupedStates = (dto?: GroupedStatesDto): CountryStateGroup[] =>
  dto?.map((item) => ({
    country: { code: item.country.code, name: item.country.name },
    states: item.stateList.map((s: StateProvinceDto): StateProvince => ({
      code: s.code,
      name: s.name,
    })),
  })) ?? [];

const mapResourceLists = (dto?: ResourceListDto): ResourceList =>
  Object.fromEntries(
    (dto?.resourceLists ?? []).map((group) => [
      group.name,
      group.item.map((i): ResourceListItem => ({ key: i.key, value: i.value })),
    ]),
  );

const mapDocumentList = (dto?: ResourceListDto): DocumentList => {
  const documentTypeGroup = dto?.resourceLists?.find(
    (group) => group.name === 'TimaticDocumentType.List',
  );

  return (
    documentTypeGroup?.item.map((item): ResourceListItem => ({
      key: item.key,
      value: item.value,
    })) ?? []
  );
};

const mapCarriers = (dto?: CarrierDto): Carrier[] =>
  dto?.carriers?.map((item) => ({
    code: item.carrierCode,
    name: item.carrierName,
    description: item.carrierDescription,
    logoImage: item.column1DesktopImage,
    tailImage: item.tailImage,
    tailImageTransparent: item.tailImageTransparent,
    website: item.carrierSite,
  })) ?? [];

const mapQuestionnaire = (dto?: QuestionnaireDto): Questionnaire => {
  if (!dto) return {};
  return Object.fromEntries(
    Object.entries(dto).map(([key, entry]) => [
      key,
      {
        questionKey: entry.QuestionniareKey,
        items: entry.QuestionniareItems.map((item): Question => ({
          Value: item.Value,
          LinkText: item.LinkText,
          MobileLink: item.MobileLink,
          popover: {
            Key: item.PopoverKey,
            Value: item.PopoverValue,
            ImageUrl: item.ImageUrl ?? '',
            TBImageUrl: item.TBImageUrl ?? '',
            MBImageUrl: item.MBImageUrl ?? '',
            IsTim: item.IsTim,
          },
        })),
      } as QuestionEntry,
    ]),
  );
};

const mapCodeTypeList = (dto?: CodeTypeListDto): CodeTypeList =>
  dto?.codeTypeList.map(
    (item) =>
      ({
        codeID: item.codeID,
        codeType: item.codeType,
        codeName: item.codeName,
        shortCodeName: item.shortCodeName,
        mobileShortCodeName: item.mobileShortCodeName,
        qty: item.qty,
        group: item.group,
        bookingReceiptImage: item.bookingReceiptImage,
        receiptDisplay: item.receiptDisplay,
        reviewDisplay: item.reviewDisplay,
        shortDescription: item.shortDescription,
        longDescription: item.longDescription,
        codeDescription: item.codeDescription,
        cartDisplayFormat: item.cartDisplayFormat,
        cartDisplay: item.cartDisplay,
        displayGroup: item.displayGroup,
        ssrIconImage: item.ssrIconImage,
        equipmentDetails: item.equipmentDetails?.map((detail): EquipmentDetail => ({
          title: detail.title,
          description: detail.description,
        })),
      }) as CodeTypeItem,
  ) ?? [];

const mapMealsList = (dto?: MealsListDto): MealsList =>
  dto?.meals?.map((item) => ({
    name: item.name,
    code: item.code,
    description: item.description,
    masterCode: item.masterCode,
  })) ?? [];

const mapEquipmentTypesList = (dto?: EquipmentTypesListDto): EquipmentTypesList => {
  if (!dto?.category) return [];
  return dto.category.flatMap((cat) =>
    cat.item.map((equip) => ({
      key: equip.key,
      value: equip.value,
      description: equip.description,
      meta: equip.meta,
    })),
  );
};

const mapFareBrands = (dto?: FareBrandsDto): FareBrandsList =>
  dto?.fareBrands?.map((brand) => ({
    code: brand.code,
    name: brand.name,
    longDescription: brand.longDescription,
    alternateDisplay: brand.alternateDisplay,
    receiptDisplay: brand.receiptDisplay,
    priority: brand.priority,
    includes: brand.includes,
    rules: brand.rules,
  })) ?? [];

const mapExceptionList = (dto?: ExceptionListDto): ExceptionList => dto ?? {};

const mapRouteMessages = (dto?: RouteMessagesDto): RouteMessagesList =>
  dto?.routeMessages?.map((item): RouteMessage => ({
    key: item.key,
    title: item.title,
    message: item.message,
  })) ?? [];

const mapAnnouncements = (dto?: AnnouncementsDto): AnnouncementsList =>
  dto?.routeMessages?.map((item): Announcement => ({
    key: item.key,
    title: item.title,
    message: item.message,
  })) ?? [];

const mapOlciBanners = (dto?: OlciBannersDto): OlciBanners =>
  dto?.map((item): OlciBanner => ({
    code: item.code,
    desktopImage: item.desktopImage,
    mobileImage: item.mobileImage,
    url: item.url,
  })) ?? [];

const mapOffers = (dto?: OfferCarouselsDto): OfferCarouselsList =>
  dto?.map((item): OfferCarousel => ({
    image: item.image,
    mobileImage: item.mobileImage,
    title: item.title,
    description: item.description,
    buttonText: item.buttonText,
    link: item.link,
  })) ?? [];

const mapLifeBanners = (dto?: LifeEshopDto): LifeBannersList =>
  dto?.map((item): LifeBanner => ({
    code: item.code,
    desktopImage: item.desktopImage,
    mobileImage: item.mobileImage,
    url: item.url,
  })) ?? [];

const mapUpgradeOffers = (dto?: UpgradeOffersDto): UpgradeOffersList =>
  dto?.map((item): UpgradeOffer => ({
    image: item.image,
    mobileImage: item.mobileImage,
    title: item.title,
    offer: item.offer,
    buttonText: item.buttonText,
    footer: item.footer,
    desktopAlignment: item.desktopAlignment,
  })) ?? [];

const mapDetailUpgrades = (dto?: DetailUpgradesDto): DetailUpgradeList =>
  dto?.map((row) => ({
    name: row.name,
    rows: row.rows.map((items) =>
      items.map((item) => ({
        title: item.title,
        subtitle: item.subtitle,
        image: item.image,
      })),
    ),
  })) ?? [];

const mapRandomSeatSelectionRules = (dto?: unknown): RandomSeatSelectionRules => {
  if (!dto || typeof dto !== 'object') {
    return { description1: '', description2: '', description3: '' };
  }
  const obj = dto as Record<string, unknown>;
  return {
    description1: typeof obj.description1 === 'string' ? obj.description1 : '',
    description2: typeof obj.description2 === 'string' ? obj.description2 : '',
    description3: typeof obj.description3 === 'string' ? obj.description3 : '',
  };
};

const mapAppSettings = (dto?: AppSettingsDto): AppSettings => {
  if (!dto) return {};
  const result: AppSettings = {};
  for (const [key, value] of Object.entries(dto)) {
    result[key] = {
      IfeEnabled: value.IfeEnabled,
      SportsEnabled: value.SportsEnabled,
      redemptionEnabled: value.redemptionEnabled,
      IsDeepLinkEnabled: value.IsDeepLinkEnabled,
      VwoEnable: value.VwoEnable,
      DefaultSortOrder: value.DefaultSortOrder,
      ConsumerSortOrder: value.ConsumerSortOrder,
      TASortOrder: value.TASortOrder,
      ConsumerConnectionSortOrder: value.ConsumerConnectionSortOrder,
      VwoAdditionalReference: value.VwoAdditionalReference,
      homepageUrl: value.homepageUrl,
      skywardsBenefits: value.skywardsBenefits,
      skywardsEarning: value.skywardsEarning,
      skywardsSpending: value.skywardsSpending,
      skywardsMembershipTiers: value.skywardsMembershipTiers,
      skywardsPartners: value.skywardsPartners,
      skywardsFaq: value.skywardsFaq,
      skywardsContact: value.skywardsContact,
      skywardsTerms: value.skywardsTerms,
      ...(value.skywardsMembershipNumberMinLength && {
        skywardsMembershipNumberMinLength: value.skywardsMembershipNumberMinLength,
      }),
      ...(value['scandIT-license'] && { 'scandIT-license': value['scandIT-license'] }),
      ...(value.ScandITErrorTimeOut && { ScandITErrorTimeOut: value.ScandITErrorTimeOut }),
      ...(value.gtmId && { gtmId: value.gtmId }),
    };
  }
  return result;
};

export const mapMasterDataDto = (dto: MasterDataDto, language: string): MasterData => ({
  airports: dto.airports.category.flatMap((cat) =>
    cat.item.map((item) => ({
      code: item.key,
      name: item.value,
      city: item.city,
      country: item.country,
      countryCode: item.c,
      timezone: item.ll,
    })),
  ),
  countries: mapCountries(dto.countries, language),
  groupedStates: mapGroupedStates(dto.groupedStates),
  carriers: mapCarriers(dto.carriers),
  resourceLists: mapResourceLists(dto.resourceLists),
  documentList: mapDocumentList(dto.documentList),
  questionnaire: mapQuestionnaire(dto.questionnaire),
  codeTypeList: mapCodeTypeList(dto.codeTypeList),
  mealsList: mapMealsList(dto.mealsList),
  equipmentTypesList: mapEquipmentTypesList(dto.equipmentTypesList),
  fareBrands: mapFareBrands(dto.fareBrands),
  exceptionList: mapExceptionList(dto.exceptionList),
  routeMessages: mapRouteMessages(dto.routeMessages),
  announcements: mapAnnouncements(dto.announcements),
  olciBanners: mapOlciBanners(dto?.olciBanners),
  offers: mapOffers(dto.offers),
  lifeBanners: mapLifeBanners(dto.lifeBanners),
  upgradeOffers: mapUpgradeOffers(dto.upgradeOffers),
  detailUpgrades: mapDetailUpgrades(dto.detailUpgrades),
  randomSeatSelectionRules: mapRandomSeatSelectionRules(dto.randomSeatSelectionRules),
  appSettings: mapAppSettings(dto.appSettings),
  terminals: dto.terminals ?? [],
  translations: {},
  language,
  fetchedAt: new Date().toISOString(),
});
